import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3206Context } from '../U3206Context';
import { doDeleteData, doSearch, luHandleAppiError, type U3206AiUserGroupSearchRow } from '../U3206Service';
import { NS } from '../U3206';
import U3206AiUserGroupDeleteDlg from './common/U3206AiUserGroupDeleteDlg';
import { U3206AiUserGroupListToolBar } from './list/U3206AiUserGroupListToolBar';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

function parseBooleanGridValue(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (typeof o.value === 'boolean') return o.value;
    if (o.value === 'true' || o.value === 1) return true;
    if (o.value === 'false' || o.value === 0) return false;
  }
  return null;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'code',
      label: t('lblCode', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'name',
      label: t('lblName', { ns: NS }),
      widthClass: 'w-[240px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'active',
      label: t('lblActive', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => {
        const b = parseBooleanGridValue(value);
        if (b === true) return t('lblActiveYes', { ns: NS });
        if (b === false) return t('lblActiveNo', { ns: NS });
        return null;
      },
    },
  ];
}

const U3206AiUserGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3206Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  useEffect(() => {
    const listTitle = t('title', { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3206AiUserGroupSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3206AiUserGroupSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version }),
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: row.code ?? row.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const listHandlerActions: U2000TableHandlers<U3206AiUserGroupSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3206AiUserGroupSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3206');
      navigate('new');
    },
    onModify: () => {
      const row = gridStateHandlers.getSelectedRows()[0];
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3206');
      navigate(`${row?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3206AiUserGroupDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          <U3206AiUserGroupListToolBar gridStateHandlers={gridStateHandlers} handlerActions={listHandlerActions} />
        }
        handlerActions={listHandlerActions}
        label={t('lblGroupList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </>
  );
};

export default U3206AiUserGroupList;
